import { Injectable } from '@angular/core';

import { DocumentNode } from '@apollo/client/link/core/types';
import { Apollo, Query, gql } from 'apollo-angular';

import {
  Customer,
  Employee,
  FormConfiguration,
  QueryFormConfigurationArgs,
  StoreConfiguration
} from '@hdms/shared/common-types';
import { removeFieldsFromDocument } from '@hdms/shared/data-access';
import { FeatureToggleService } from '@hdms/shared/util-core';

import { FT_ADDRESS_CHECK_V2_NEW_SHIPTO } from './feature-toggle.tokens';

export interface AddressCreateConfigurationResponse {
  formConfiguration: FormConfiguration;
  loggedInContext?: Customer | Employee;
  storeConfiguration: StoreConfiguration;
}

export interface QueryAddressCreateConfigurationDetailArgs extends QueryFormConfigurationArgs {
  isAddress: boolean;
}

export function toggleHiddenOnFormField(doc: DocumentNode, ftService: FeatureToggleService): DocumentNode {
  if (!ftService.getToggleById(FT_ADDRESS_CHECK_V2_NEW_SHIPTO)) {
    return removeFieldsFromDocument(doc, ['hiddenOnForm']);
  }

  return doc;
}

@Injectable({
  providedIn: 'root'
})
export class AddressCreateConfigGqlService extends Query<
  AddressCreateConfigurationResponse,
  QueryAddressCreateConfigurationDetailArgs
> {
  override document = gql`
    query AddressCreateConfig($type: FormType!, $localization: LocalizationInput!, $isAddress: Boolean!) {
      loggedInContext @include(if: $isAddress) {
        id
        ... on Customer {
          companyAccount {
            id
            originId
            address {
              company
            }
          }
        }
        ... on Employee {
          customerCompanyAccount {
            id
            originId
            address {
              company
            }
          }
        }
      }
      formConfiguration(type: $type, localization: $localization) {
        id
        fields {
          id
          readOnly
          type
          upperCase
          hiddenOnForm
          validation {
            maxLength
            pattern
            required
          }
          values(localization: $localization) {
            id
            name
          }
        }
      }
      storeConfiguration(localization: $localization) @include(if: $isAddress) {
        id
        common {
          addressCheck
          addressCheckCaseType
        }
      }
    }
  `;

  // required only for testing w/ code-coverage due to https://github.com/angular/angular/issues/31337
  constructor(apollo: Apollo, ftService: FeatureToggleService) {
    super(apollo);

    this.document = toggleHiddenOnFormField(this.document, ftService);
  }
}
